import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  doc,
  getDoc,
  deleteDoc,
  collection,
  getDocs,
  setDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../context/AuthContext';
import QRCodeCanvas from 'react-qrcode-logo';

// Generate a random invite code
function generateCode(length = 11) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [event, setEvent] = useState(null);
  const [rsvps, setRsvps] = useState({ attending: [], maybe: [], not_attending: [] });
  const [userRsvp, setUserRsvp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // QR + invite state
  const [showQR, setShowQR] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);
  const [generatingInvite, setGeneratingInvite] = useState(false);

  // ── Fetch event + RSVPs ──────────────────────────────────────────────────
  const fetchEventDetails = async () => {
    try {
      const eventSnap = await getDoc(doc(db, 'events', id));
      if (!eventSnap.exists()) { setLoading(false); return; }
      const eventData = { id: eventSnap.id, ...eventSnap.data() };
      setEvent(eventData);

      const rsvpSnap = await getDocs(collection(db, 'events', id, 'rsvps'));
      const all = { attending: [], maybe: [], not_attending: [] };
      rsvpSnap.docs.forEach((d) => {
        const rsvp = { id: d.id, ...d.data() };
        if (all[rsvp.status]) all[rsvp.status].push(rsvp);
      });
      setRsvps(all);

      if (user) {
        const myRsvp = rsvpSnap.docs.find((d) => d.id === user.uid);
        setUserRsvp(myRsvp ? { id: myRsvp.id, ...myRsvp.data() } : null);
      }

      if (eventData.inviteCode) {
        setInviteLink(`${window.location.origin}/invite/${eventData.inviteCode}`);
      }
    } catch (error) {
      console.error('Failed to fetch event details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEventDetails();
    // eslint-disable-next-line
  }, [id, user]);

  // ── RSVP ────────────────────────────────────────────────────────────────
  const handleRSVP = async (status) => {
    if (!user) return;
    try {
      await setDoc(doc(db, 'events', id, 'rsvps', user.uid), {
        status,
        userName: user.displayName || user.email,
        userEmail: user.email,
        updatedAt: serverTimestamp(),
      });
      fetchEventDetails();
    } catch (e) {
      console.error('RSVP failed:', e);
    }
  };

  // ── Delete event ─────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, 'events', id));
      navigate('/dashboard');
    } catch (error) {
      alert('Failed to delete event');
    }
  };

  // ── Generate invite link ──────────────────────────────────────────────────
  const handleGenerateInvite = async () => {
    setGeneratingInvite(true);
    try {
      const code = generateCode();
      await updateDoc(doc(db, 'events', id), { inviteCode: code });
      await setDoc(doc(db, 'invites', code), {
        eventId: id,
        createdAt: serverTimestamp(),
      });
      const link = `${window.location.origin}/invite/${code}`;
      setInviteLink(link);
    } catch (e) {
      console.error('Failed to generate invite:', e);
    } finally {
      setGeneratingInvite(false);
    }
  };

  const handleCopyInvite = () => {
    navigator.clipboard.writeText(inviteLink);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });

  // ── Guards ───────────────────────────────────────────────────────────────
  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}><h2>Loading event...</h2></div>;
  if (!event) return (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <h2>Event not found</h2>
      <Link to="/dashboard">Back to Dashboard</Link>
    </div>
  );

  const isCreator = user && event.creatorId === user.uid;
  const isAttending = userRsvp?.status === 'attending';
  const totalRsvps = rsvps.attending.length + rsvps.maybe.length + rsvps.not_attending.length;

  const ticketData = JSON.stringify({
    eventId: id,
    userId: user?.uid,
    userName: user?.displayName || user?.email,
    eventTitle: event.title,
    status: userRsvp?.status || 'attending',
  });

  return (
    <div style={{ maxWidth: '800px', margin: '2rem auto', padding: '0 2rem' }}>

      {/* Back */}
      <div style={{ marginBottom: '2rem' }}>
        <Link to="/dashboard" style={{ color: '#6366f1', textDecoration: 'none' }}>
          ← Back to Dashboard
        </Link>
      </div>

      {/* Image */}
      {event.imageUrl && (
        <img src={event.imageUrl} alt={event.title} style={{
          width: '100%', height: '300px', objectFit: 'cover',
          borderRadius: '8px', marginBottom: '2rem',
        }} />
      )}

      {/* Title + creator actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
        <div>
          <h1 style={{ margin: '0 0 0.5rem 0', color: '#1f2937' }}>{event.title}</h1>
          <span style={{
            backgroundColor: '#dbeafe', color: '#1e40af',
            padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.9rem',
          }}>{event.category}</span>
        </div>
        {isCreator && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Link to={`/events/${id}/edit`} style={{
              backgroundColor: '#6366f1', color: 'white',
              padding: '0.5rem 1rem', borderRadius: '4px', textDecoration: 'none',
            }}>Edit</Link>
            <Link to={`/events/${id}/scan`} style={{
              backgroundColor: '#7c3aed', color: 'white',
              padding: '0.5rem 1rem', borderRadius: '4px', textDecoration: 'none',
            }}>Scan Tickets</Link>
            <button onClick={() => setShowDeleteConfirm(true)} style={{
              backgroundColor: '#ef4444', color: 'white',
              padding: '0.5rem 1rem', border: 'none', borderRadius: '4px', cursor: 'pointer',
            }}>Delete</button>
          </div>
        )}
      </div>

      {/* Event details */}
      <div style={{
        backgroundColor: 'white', padding: '2rem', borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '2rem',
      }}>
        <p style={{ fontSize: '1.1rem', margin: '0.5rem 0' }}>📅 <strong>{formatDate(event.date)}</strong></p>
        <p style={{ fontSize: '1.1rem', margin: '0.5rem 0' }}>🕐 {event.startTime} - {event.endTime}</p>
        <p style={{ fontSize: '1.1rem', margin: '0.5rem 0' }}>📍 {event.location?.address}, {event.location?.city}</p>
        <p style={{ fontSize: '1rem', margin: '0.5rem 0', color: '#6b7280' }}>
          👤 Hosted by {event.creatorName || 'Event Creator'}
        </p>
        {event.description && (
          <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
            <h3 style={{ marginBottom: '0.5rem' }}>Description</h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6' }}>{event.description}</p>
          </div>
        )}
      </div>

      {/* MAP — Google Maps iframe, no package needed */}
      <div style={{
        backgroundColor: 'white', padding: '2rem', borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '2rem',
      }}>
        <h3 style={{ marginBottom: '1rem' }}>📍 Venue Location</h3>
        <iframe
          title="Event location"
          width="100%"
          height="280"
          style={{ border: 0, borderRadius: '8px' }}
          loading="lazy"
          allowFullScreen
          src={`https://maps.google.com/maps?q=${encodeURIComponent(
            `${event.location?.address}, ${event.location?.city}`
          )}&output=embed`}
        />
        <p style={{ marginTop: '0.75rem', color: '#6b7280', fontSize: '0.9rem' }}>
          {event.location?.address}, {event.location?.city}
        </p>
      </div>

      {/* QR TICKET */}
      <div style={{
        backgroundColor: 'white', padding: '2rem', borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '2rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>🎫 Your Ticket</h3>
          <button
            onClick={() => setShowQR(!showQR)}
            style={{
              backgroundColor: '#6366f1', color: 'white',
              padding: '0.5rem 1rem', border: 'none', borderRadius: '6px',
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold',
            }}
          >
            {showQR ? 'Hide QR' : 'Show QR Code'}
          </button>
        </div>

        {!isAttending && !showQR && (
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
            RSVP as attending below to get your ticket QR code.
          </p>
        )}

        {showQR && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: '1rem', padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '8px',
          }}>
            <QRCodeCanvas
              value={ticketData}
              size={200}
              bgColor="#ffffff"
              fgColor="#6366f1"
              qrStyle="dots"
              eyeRadius={6}
            />
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontWeight: 'bold', margin: 0, color: '#1f2937' }}>{event.title}</p>
              <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0.25rem 0' }}>
                {formatDate(event.date)} · {event.startTime}
              </p>
              <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>
                {user?.displayName || user?.email}
              </p>
            </div>
            <span style={{
              backgroundColor: '#d1fae5', color: '#065f46',
              padding: '0.25rem 0.75rem', borderRadius: '999px',
              fontSize: '0.85rem', fontWeight: 'bold',
            }}>✓ {userRsvp?.status || 'Attending'}</span>
            <p style={{ color: '#9ca3af', fontSize: '0.75rem', textAlign: 'center', margin: 0 }}>
              Show this QR code at the door to check in.
            </p>
          </div>
        )}
      </div>

      {/* INVITE LINK — creator only */}
      {isCreator && (
        <div style={{
          backgroundColor: 'white', padding: '2rem', borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '2rem',
        }}>
          <h3 style={{ marginBottom: '0.5rem' }}>🔗 Invite Guests</h3>
          <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' }}>
            Share this link so guests can view and RSVP to your event.
          </p>
          {!inviteLink ? (
            <button
              onClick={handleGenerateInvite}
              disabled={generatingInvite}
              style={{
                backgroundColor: '#6366f1', color: 'white',
                padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px',
                cursor: generatingInvite ? 'not-allowed' : 'pointer',
                fontWeight: 'bold', opacity: generatingInvite ? 0.7 : 1,
              }}
            >
              {generatingInvite ? 'Generating...' : '+ Generate Invite Link'}
            </button>
          ) : (
            <div>
              <div style={{
                display: 'flex', gap: '0.5rem', alignItems: 'center',
                backgroundColor: '#f3f4f6', padding: '0.75rem 1rem',
                borderRadius: '8px', marginBottom: '0.75rem',
              }}>
                <span style={{
                  flex: 1, fontSize: '0.85rem', color: '#374151',
                  wordBreak: 'break-all', fontFamily: 'monospace',
                }}>{inviteLink}</span>
                <button
                  onClick={handleCopyInvite}
                  style={{
                    backgroundColor: inviteCopied ? '#10b981' : '#6366f1',
                    color: 'white', padding: '0.5rem 1rem',
                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '0.85rem', fontWeight: 'bold', whiteSpace: 'nowrap',
                    transition: 'background-color 0.2s',
                  }}
                >
                  {inviteCopied ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
              <button
                onClick={handleGenerateInvite}
                style={{
                  backgroundColor: 'transparent', color: '#6b7280',
                  padding: 0, border: 'none', cursor: 'pointer',
                  fontSize: '0.85rem', textDecoration: 'underline',
                }}
              >
                Generate new link
              </button>
            </div>
          )}
        </div>
      )}

      {/* RSVP section */}
      <div style={{
        backgroundColor: 'white', padding: '2rem', borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '2rem',
      }}>
        <h3 style={{ marginBottom: '1rem' }}>RSVP Status</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          {[
            { key: 'attending', label: 'Attending', color: '#16a34a', bg: '#f0fdf4' },
            { key: 'maybe', label: 'Maybe', color: '#d97706', bg: '#fef3c7' },
            { key: 'not_attending', label: 'Not Attending', color: '#dc2626', bg: '#fee2e2' },
          ].map(({ key, label, color, bg }) => (
            <div key={key} style={{ textAlign: 'center', padding: '1rem', backgroundColor: bg, borderRadius: '8px' }}>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color }}>{rsvps[key].length}</p>
              <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280' }}>{label}</p>
            </div>
          ))}
        </div>

        <h4 style={{ marginBottom: '1rem' }}>Your RSVP</h4>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {[
            { status: 'attending', label: '✅ Attending', active: '#6366f1' },
            { status: 'maybe', label: '🤔 Maybe', active: '#d97706' },
            { status: 'not_attending', label: '❌ Not Attending', active: '#dc2626' },
          ].map(({ status, label, active }) => (
            <button
              key={status}
              onClick={() => handleRSVP(status)}
              style={{
                padding: '0.6rem 1.25rem',
                border: `2px solid ${userRsvp?.status === status ? active : '#e5e7eb'}`,
                borderRadius: '8px',
                backgroundColor: userRsvp?.status === status ? active : 'white',
                color: userRsvp?.status === status ? 'white' : '#374151',
                cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem',
                transition: 'all 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Guest list — creator only */}
      {isCreator && totalRsvps > 0 && (
        <div style={{
          backgroundColor: 'white', padding: '2rem', borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginBottom: '1rem' }}>Guest List ({totalRsvps})</h3>
          {[
            { key: 'attending', label: '✅ Attending', color: '#16a34a' },
            { key: 'maybe', label: '❓ Maybe', color: '#d97706' },
            { key: 'not_attending', label: '❌ Not Attending', color: '#dc2626' },
          ].map(({ key, label, color }) =>
            rsvps[key].length > 0 && (
              <div key={key} style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ color, marginBottom: '0.5rem' }}>{label} ({rsvps[key].length})</h4>
                {rsvps[key].map((rsvp) => (
                  <p key={rsvp.id} style={{ margin: '0.25rem 0', color: '#6b7280' }}>
                    {rsvp.userName} {rsvp.userEmail ? `(${rsvp.userEmail})` : ''}
                  </p>
                ))}
              </div>
            )
          )}
        </div>
      )}

      {/* Delete modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white', padding: '2rem', borderRadius: '8px',
            maxWidth: '400px', width: '90%',
          }}>
            <h3 style={{ marginBottom: '1rem' }}>Delete Event?</h3>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
              This action cannot be undone and all RSVPs will be removed.
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button onClick={handleDelete} style={{
                flex: 1, backgroundColor: '#ef4444', color: 'white',
                padding: '0.75rem', border: 'none', borderRadius: '4px',
                fontWeight: 'bold', cursor: 'pointer',
              }}>Delete</button>
              <button onClick={() => setShowDeleteConfirm(false)} style={{
                flex: 1, backgroundColor: '#e5e7eb', color: '#374151',
                padding: '0.75rem', border: 'none', borderRadius: '4px',
                fontWeight: 'bold', cursor: 'pointer',
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;
